import Button from "@mui/material/Button";
import { SxProps } from "@mui/system";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import React from "react";

import { Tooltip } from "../../molecules";

// Define allowed sizes and variants
type ButtonSize = "small" | "medium" | "large";
export type ButtonVariant =
  | "contained"
  | "outlined"
  | "report"
  | "reportBlue"
  | "grey"
  | "dark"
  | "white"
  | "whiteOutlined"
  | "blue"
  | "error"
  | "disabled";

interface PimoButtonProps {
  size: ButtonSize;
  variant: ButtonVariant;
  text: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  tooltip?: string;
  loading?: boolean;
}

interface ButtonVariantProps {
  variant?: "contained" | "outlined" | "text";
  color?:
    | "primary"
    | "secondary"
    | "error"
    | "warning"
    | "info"
    | "success"
    | "inherit";
  disabled?: boolean;
  sx?: SxProps & { hoverBackgroundColor?: string };
}

const variantPropsMap: Record<ButtonVariant, ButtonVariantProps> = {
  contained: { variant: "contained", sx: { color: "#fff" } },
  outlined: { variant: "outlined", color: "primary" },
  report: {
    sx: {
      backgroundColor: "#F86200",
      color: "#fff",
      hoverBackgroundColor: "#ff8c42",
    },
  },
  reportBlue: {
    sx: {
      backgroundColor: "#003781",
      color: "#fff",
      hoverBackgroundColor: "#00265c",
    },
  },
  grey: {
    variant: "contained",
    color: "inherit",
    sx: { hoverBackgroundColor: "#e0e0e0" },
  },
  dark: {
    sx: {
      backgroundColor: "#374151",
      color: "#fff",
      hoverBackgroundColor: "#1f2937",
    },
  },
  white: {
    sx: {
      backgroundColor: "#fff",
      color: "#000",
      hoverBackgroundColor: "#f5f5f5",
    },
  },
  whiteOutlined: {
    variant: "outlined",
    color: "inherit",
    sx: {
      backgroundColor: "#fff",
      color: "#000",
      borderColor: "#c4c4c4",
      hoverBackgroundColor: "#f5f5f5",
    },
  },
  blue: { sx: { backgroundColor: "#313849", color: "#fff" } },
  error: {
    sx: {
      backgroundColor: "#D32F2F",
      color: "#fff",
      hoverBackgroundColor: "#C62828",
    },
  },
  disabled: { variant: "contained", color: "primary", disabled: true },
};

const getMuiProps = (variant: ButtonVariant) =>
  variantPropsMap[variant] ?? { variant: "contained", color: "primary" };

const sizeStyles: Record<ButtonSize, { padding: string; height: string }> = {
  small: { padding: "0px 8px", height: "28px" },
  medium: { padding: "0px 12px", height: "36px" },
  large: { padding: "16px 32px", height: "48px" },
};

export const PMButton: PimoReactComponent<PimoButtonProps, "click"> = ({
  size = "medium",
  variant,
  text,
  icon,
  fireEvent,
  onClick,
  tooltip,
  ...props
}) => {
  const muiProps = getMuiProps(variant);
  const isDisabled = variant === "disabled" || muiProps.disabled;

  const PMButtonCmp = (
    <Button
      {...props}
      {...muiProps}
      startIcon={icon}
      disabled={isDisabled}
      onClick={
        isDisabled
          ? undefined
          : () => {
              if (onClick) onClick();
              if (fireEvent) fireEvent("click");
            }
      }
      sx={{
        padding: sizeStyles[size].padding,
        borderRadius: "4px",
        ...muiProps.sx,
        textTransform: "none",
        boxShadow: "none",
        fontSize: "14px",
        height: sizeStyles[size].height,
        transition: "background-color 0.2s, color 0.2s",
        "&:hover": {
          boxShadow: "none",
          backgroundColor: muiProps.sx?.hoverBackgroundColor || undefined,
        },
      }}
    >
      {text}
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="bottom">
        {/* Need to add a span wrapper, because tooltips dont show on disabled buttons */}
        <span>{PMButtonCmp}</span>
      </Tooltip>
    );
  }

  return PMButtonCmp;
};
